import * as angular from 'angular';
import * as angulartics from 'angulartics';
import * as angularticsGoogleAnalytics from 'angulartics-google-analytics';
import * as angularTranslate from 'angular-translate';
import * as angularAria from 'angular-aria';
import * as angularAnimate from 'angular-animate';

import dropdown from 'angular-ui-bootstrap/src/dropdown';
import modal from 'angular-ui-bootstrap/src/modal';
import popover from 'angular-ui-bootstrap/src/popover';
import tooltip from 'angular-ui-bootstrap/src/tooltip';

import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as relativeTime from 'dayjs/plugin/relativeTime';
import * as $ocLazyLoad from 'oclazyload';
import { logProvider } from './configs/log-provider.config';
import { compileProviderConfig } from './configs/compiler.config';
import { uiDatepickerConfig } from './configs/ui-datepicker.config';
import { updateSettings } from './runs/update-settings.run';
import { GroupSettingsUserListItemController } from './controllers/group-settings-user-list-item.controller';
import { environmentConstants } from './constants/environment-constants.constant';
import { uibModalDecoratorModule } from 'go-modules/$uib-modal-decorator';
import { groupCreatorModule } from 'go-modules/group-creator';
import { updateManagerModule } from 'go-modules/update-manager';
import { goModal } from 'go-modules/modals';
import { fineUploaderModule } from 'go-modules/fine-uploader';
import { richDropdownModule } from 'go-modules/rich-dropdown';
import { goPopoverModule } from 'go-modules/go-popover';
import { goDateTimePickerModule } from 'go-modules/datetimepicker';
import { mediaPreviewModule } from 'go-modules/media-preview';
import { feedbackSessionModule } from 'go-modules/feedback-session';
import { sessionEditorModule } from 'go-modules/session-editor';
import { sessionManager } from 'go-modules/session-manager';
import { presenterListModule } from 'go-modules/presenter-list';
import { courseEditorModule } from 'go-modules/course-editor';
import { masqueradeModule } from 'go-modules/masquerade';
import { goPdfModule } from 'go-modules/go-pdf';
import { mediaPlayerModule } from 'go-modules/media-player';
import { rubricModule } from 'go-modules/rubric';
import { goPubnubModule } from 'go-modules/go-pubnub';
import { goAppEnvModule } from 'go-modules/go-app-env';
import { timeagoModule } from 'go-modules/timeago';
import { helpUrlsModule } from 'go-modules/help-urls';
import { detectModule } from 'go-modules/detect';
import { commonFiltersModule } from 'go-modules/common-filters';
import { formValidationModule } from 'go-modules/form-validation';
import { sessionEditorPanelModule } from 'go-modules/session-editor-panel';
import { sessionCreationWizardModule } from 'go-modules/session-creation-wizard';
import { confirmModalModule } from 'go-modules/modals/confirm';
import { appRunModule } from 'go-modules/app-run';
import { allModelsModule } from 'go-modules/models';
import { ngxFeatureFlagServiceModule } from '@go-modules/services/feature-flag';
import { ngxTaskServiceModule } from '@go-modules/services/task';
import { groupsIndexModule } from 'go-modules/groups-index';
import { responsiveViewModule } from 'go-modules/responsive-view';
import { unhandledRejectionConfig } from './configs/unhandled-rejection.config';
import { messageModalModule } from 'go-modules/modals/message';
import { groupServiceModule } from 'go-modules/services/group';
import { watsonUsageTrackingModule } from 'go-modules/services/watson-usage-tracking';
import { ngxDashboardRootViewModule } from '../src/app/components/dashboard-root';
import { analyticsModule } from 'go-modules/services/analytics';
import { fullstoryModule } from 'go-modules/services/fullstory';
import { bannerNotificationsModule } from 'go-modules/services/banner-notifications';
import { goBannerModule } from 'go-modules/go-banner';
import { ariaDecoratorModule } from 'go-modules/aria-decorator';
import { dashboardServicesModule } from './services';
import { ngxResetPasswordModule } from '@go-modules/components/auth/reset-password';
import { ngxForgotPasswordModule } from '@go-modules/components/auth/forgot-password';
import { ngxGroupConfirmModule } from '@go-modules/components/auth/group-confirm';

// Circular dependency
import 'go-modules/universal-media-chooser';

// Upgraded Angular imports
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { goLogoModule } from '@dashboard/app/components/go-logo';
import { eventServiceModule } from '@go-modules/services/event';
import { downgradeComponent, downgradeInjectable } from '@angular/upgrade/static';
import { goLocalizeHelperModule } from 'go-modules/translation-helper';
import { activityReportPanelModule } from 'go-modules/activity-report-panel';
import { groupSettingsPanelModule } from 'go-modules/group-settings-panel';
import { csvReportModule } from 'go-modules/csv-report';
import { goTourModule } from 'go-modules/go-tour';
import { transactionModelModule } from 'go-modules/models/transaction';
import { PWAModule } from 'dashboard/services/pwa';
import { DashboardSignupWrapperComponent } from '@dashboard/app/components/signup/dashboard-signup-wrapper/dashboard-signup-wrapper.component';
import { LoginWrapperComponent } from '@dashboard/app/components/login-wrapper/login-wrapper.component';
import { NgxAuthService } from '@go-modules/services/auth/auth.service';
// Circular dependency
import 'go-modules/activity-editor-panel';
import { Router } from '@angular/router';

dayjs.extend(utc);
dayjs.extend(relativeTime);

export const dashboardModule = angular.module('app', [
	angularAria,
	angularAnimate,
	allModelsModule,
	updateManagerModule,
	goModal,
	groupServiceModule,
	fineUploaderModule,
	richDropdownModule,
	goPopoverModule,
	goDateTimePickerModule,
	mediaPreviewModule,
	feedbackSessionModule,
	sessionEditorModule,
	sessionManager,
	presenterListModule,
	courseEditorModule,
	masqueradeModule,
	goPdfModule,
	mediaPlayerModule,
	rubricModule,
	goAppEnvModule,
	goPubnubModule,
	timeagoModule,
	helpUrlsModule,
	detectModule,
	commonFiltersModule,
	formValidationModule,
	tooltip,
	popover,
	modal,
	dropdown,
	angulartics,
	angularticsGoogleAnalytics,
	angularTranslate,
	groupsIndexModule,
	groupCreatorModule,
	groupSettingsPanelModule,
	sessionEditorPanelModule,
	uibModalDecoratorModule,
	sessionCreationWizardModule,
	confirmModalModule,
	appRunModule,
	messageModalModule,
	responsiveViewModule,
	ngxFeatureFlagServiceModule,
	ngxTaskServiceModule,
	$ocLazyLoad,
	ngxDashboardRootViewModule,
	analyticsModule,
	fullstoryModule,
	bannerNotificationsModule,
	goBannerModule,
	goLocalizeHelperModule,
	ariaDecoratorModule,
	watsonUsageTrackingModule,
	csvReportModule,
	PWAModule,
	dashboardServicesModule,
	'universal-media-chooser',
	goLogoModule,
	eventServiceModule,
	transactionModelModule,
	activityReportPanelModule.name,
	goTourModule,
	ngxResetPasswordModule,
	ngxForgotPasswordModule,
	ngxGroupConfirmModule,
	'activity-editor-panel'
])
	.config(logProvider)
	.directive('ngxDashboardSignupWrapper', downgradeComponent({ component: DashboardSignupWrapperComponent }))
	.directive('ngxLoginWrapper', downgradeComponent({ component: LoginWrapperComponent }))
	.factory('ngxAuthService', downgradeInjectable(NgxAuthService) as any)
	.factory('ngxGoModalService', downgradeInjectable(GoModalService))
	.factory('ngxTranslationService', downgradeInjectable(TranslateService))
	.config(unhandledRejectionConfig)
	.config(uiDatepickerConfig)
	.constant('ENVIRONMENT_CONSTANTS', environmentConstants)
	.config(compileProviderConfig)
	.run(updateSettings)
	.controller('GroupSettingsUserListItemController', GroupSettingsUserListItemController)
	.factory('ngRouter', downgradeInjectable(Router))
	.name;
